import { assertWriteIdentity, captureWriteIdentity, type WriteIdentity } from '@/core/api/http'
import {
  apiDelete,
  apiFirst,
  apiGetOneOrNull,
  apiListAll,
  apiPatch,
  apiPost,
  apiPut,
  type CoachingRow,
  type Participant,
} from '@/core/api/resources'
export type { Participant } from '@/core/api/resources'
import { ApiError } from '@/core/api/http'
import { isUniqueViolation } from '@/core/lib/dbError'
import { me } from './session'
import { listFiles, removeFile } from './storage'
import { descriptionFor, subtypeFor } from '@/variants'
import { aufgabeIdFor, positionForAufgabeName, AUFGABE, type AufgabeKey } from './aufgabe'
import { isNachweisDocName } from '@/core/lib/nachweisDocName'
import { bySheetName, isSheet } from '@/core/lib/sheets'
import type { DashboardCoaching, RueckzugGrundArt, Stage } from '@/core/types'

import { NO_UE_CAP } from '@/variants/azav/lib/noUeCap'
import type { AvgsPromotion } from '@/variants/azav/flows/avgsPromote'
import type { CoachingVoucherPatch } from '@/variants/azav/flows/bewilligungAdopt'

function throwWriteError(context: string, error: { message: string }): never {
  throw new Error(
    `${context} konnte nicht geschrieben werden — fehlende Berechtigung oder noch nicht angewendete Migration: ${error.message}`,
  )
}

const statusIds = new Map<Stage, string>()
async function statusIdFor(stage: Stage): Promise<string> {
  const hit = statusIds.get(stage)
  if (hit) return hit
  const { variant } = await me()
  const candidates = descriptionFor(variant).statusNameCandidatesFor(stage)

  const data = await apiListAll('/status', { name__in: candidates })

  const row = data.find((r) => r.name === candidates[0]) ?? data[0]
  if (!row) {

    throw new Error(
      `Status "${stage}" ist in der Datenbank noch nicht angelegt (Migration evtl. noch nicht angewendet?).`,
    )
  }
  statusIds.set(stage, row.id)
  return row.id
}

export type BoardBudget = {
  zeitl_umf_max_ue: number
  gueltigkeit_beginn: string
  gueltigkeit_ende: string
  massnahme: { ue_anzahl: number } | null
}
export type BoardCoaching = Pick<CoachingRow, 'id' | 'members' | 'agentur' | 'teilnahme'> & {
  secondCoach?: Pick<NonNullable<CoachingRow['members']>, 'id' | 'vorname' | 'nachname'> | null
  budget: BoardBudget | null
}

type BoardSitzung = { ue: number; datum: string }
export type BoardDokument = {
  name: string
  sitzung: BoardSitzung[] | null

  sheet: boolean
}

function noPlaceholder(d: string | null): string | null {
  return d === '1970-01-01' ? null : d
}

export async function listDashboard(
  onCursor?: (cursor: string | undefined) => void,
): Promise<DashboardCoaching[]> {
  const { organisationId, variant } = await me()

  const { subtype } = descriptionFor(variant)

  const onHeaders = (headers: Headers) => onCursor?.(headers.get('X-Admination-Live-Cursor') || undefined)
  const coachings = subtype
    ? await subtype.listBoardCoachings(organisationId, onHeaders)
    : await listCoreBoardCoachings(organisationId, undefined, onHeaders)

  // Start auxiliary reads after the cursor so replay covers intervening changes.
  const [aufgaben, teilnehmende, docs] = await Promise.all([
    apiListAll('/aufgabe', { include: ['status'] }),
    apiListAll('/teilnehmende', { organisation_id: organisationId }),
    Promise.all(coachings.map(async (c) => [c.id, subtype
      ? await subtype.listBoardDokumente(c.id)
      : await listCoreBoardDokumente(c.id)] as const)),
  ])
  const aufgabeById = new Map(aufgaben.map((sub) => [sub.id, sub]))
  const teilnehmendeById = new Map(teilnehmende.map((t) => [t.id, t]))
  const docsByCoaching = new Map(docs)
  return coachings.map((c) =>
    boardView(c, aufgabeById, teilnehmendeById, docsByCoaching.get(c.id) ?? [], variant),
  )
}

export async function loadDashboardCoaching(coachingId: string): Promise<DashboardCoaching | null> {
  const { organisationId, variant } = await me()
  const { subtype } = descriptionFor(variant)
  const [coachings, aufgaben] = await Promise.all([
    subtype
      ? subtype.listBoardCoachings(organisationId)
      : listCoreBoardCoachings(organisationId, coachingId),
    apiListAll('/aufgabe', { include: ['status'] }),
  ])
  const c = coachings.find((row) => row.id === coachingId)
  if (!c) return null
  const teilnehmendeId = c.teilnahme?.[0]?.teilnehmende_id
  const [teilnehmende, docs] = await Promise.all([
    teilnehmendeId
      ? apiListAll('/teilnehmende', { organisation_id: organisationId, id: teilnehmendeId })
      : Promise.resolve([]),
    subtype ? subtype.listBoardDokumente(c.id) : listCoreBoardDokumente(c.id),
  ])
  return boardView(
    c,
    new Map(aufgaben.map((sub) => [sub.id, sub])),
    new Map(teilnehmende.map((t) => [t.id, t])),
    docs,
    variant,
  )
}

type AufgabeWithStatus = { name: string; status?: { name: string } | null }
type BoardTeilnehmende = { vorname: string; nachname: string }

function boardView(
  c: BoardCoaching,
  aufgabeById: Map<string, AufgabeWithStatus>,
  teilnehmendeById: Map<string, BoardTeilnehmende>,
  documents: BoardDokument[],
  variant: string,
): DashboardCoaching {
  const participation = c.teilnahme?.[0]
  const task = participation?.aufgabe_id ? aufgabeById.get(participation.aufgabe_id) : undefined
  const participant = participation?.teilnehmende_id ? teilnehmendeById.get(participation.teilnehmende_id) : undefined
  const names = new Set(documents.filter((document) => !document.sheet).map((document) => document.name))
  const nachweise = documents
    .filter((document) => isNachweisDocName(document.name))
    .map((document) => ({
      name: document.name,
      sitzung: document.sitzung?.[0],
    }))
  const cap = c.budget?.zeitl_umf_max_ue ?? 0
  const catalogueUe = c.budget?.massnahme?.ue_anzahl
  return {
    id: c.id,
    name: participant ? `${participant.nachname}, ${participant.vorname}` : null,
    stage: descriptionFor(variant).stageForStatusName(task?.status?.name) ?? ('' as Stage),
    documents: [...names].filter((name) => !name.endsWith('-empty')),
    sheets: documents.filter((document) => document.sheet).map((document) => document.name).sort(bySheetName),
    sitzungDaten: Object.fromEntries(nachweise.flatMap((document) => document.sitzung ? [[document.name, document.sitzung.datum]] : [])),
    docCount: documents.length,
    ueGesamt: cap === NO_UE_CAP
      ? catalogueUe ?? null
      : cap > 0
        ? catalogueUe != null ? Math.min(cap, catalogueUe) : cap
        : null,
    ueStattgefunden: nachweise.reduce((sum, document) => sum + (document.sitzung?.ue ?? 0), 0),
    coach: [c.members?.vorname, c.members?.nachname].map((name) => (name ?? '').trim()).filter(Boolean).join(' '),
    coachId: c.members?.id ?? null,
    secondCoachId: c.secondCoach?.id ?? null,
    secondCoach: [c.secondCoach?.vorname, c.secondCoach?.nachname].filter(Boolean).join(' ').trim(),
    kostentraegerId: c.agentur?.id ?? null,
    kostentraeger: [c.agentur?.behoerde, c.agentur?.standort].filter(Boolean).join(' '),
    aufgabe: task?.name ?? null,
    statusSeit: participation?.status_seit ?? null,
    beginn: participation?.beginn ?? null,
    zurueckgezogen: participation?.zurueckgezogen_am ? {
      am: participation.zurueckgezogen_am,
      von: participation.zurueckgezogen_von ?? null,
      grundArt: (participation.zurueckgezogen_grund_art ?? null) as RueckzugGrundArt | null,
      grund: participation.zurueckgezogen_grund ?? null,
    } : null,
    eingeschraenkt: !!participation?.eingeschraenkt_at,
    gueltigkeitBeginn: c.budget ? noPlaceholder(c.budget.gueltigkeit_beginn) : null,
    gueltigkeitEnde: c.budget ? noPlaceholder(c.budget.gueltigkeit_ende) : null,
  }
}

async function listCoreBoardCoachings(
  organisationId: string,
  coachingId?: string,
  onFirstPageHeaders?: (headers: Headers) => void,
): Promise<BoardCoaching[]> {
  const rows = await apiListAll('/coaching', {
    organisation_id: organisationId,
    ...(coachingId ? { id: coachingId } : {}),
    include: ['members', 'agentur', 'teilnahme'],
  }, undefined, onFirstPageHeaders)
  return rows.map((c) => ({
    id: c.id,
    members: c.members,
    agentur: c.agentur,
    teilnahme: c.teilnahme,
    budget: null,
  }))
}

async function listCoreBoardDokumente(
  coachingId: string,
): Promise<BoardDokument[]> {
  const rows = await apiListAll('/dokumente', {
    coaching_id: coachingId,
  })
  return rows.map((d) => ({
    name: d.name,
    sitzung: null,
    sheet: isSheet(d),
  }))
}

export type CoachOption = { id: string; name: string; email?: string }

export async function listCoaches(): Promise<CoachOption[]> {
  const { organisationId } = await me()

  const data = await apiListAll('/mitgliedschaft', {
    organisation_id: organisationId,
    is_coach: true,
    include: ['members'],
  })
  return data
    .map((m) => m.members)
    .filter((u): u is NonNullable<typeof u> => u != null)
    .map((u) => ({
      id: u.id,
      email: u.email,
      name: [u.vorname, u.nachname].map((x) => (x ?? '').trim()).filter(Boolean).join(' '),
    }))
}

export async function getAssignedCoach(coachingId: string): Promise<CoachOption | null> {
  const row = await apiGetOneOrNull('/coaching/{id}', { id: coachingId }, {
    include: ['members'],
  })
  const coach = row?.members ?? null
  if (!coach) return null

  const name = [coach.vorname, coach.nachname].map((x) => (x ?? '').trim()).filter(Boolean).join(' ')
  return { id: coach.id, name }
}

export async function getCoachingOrganisationId(coachingId: string): Promise<string | null> {
  const row = await apiGetOneOrNull('/coaching/{id}', { id: coachingId })
  return row?.organisation_id ?? null
}

export async function getCoachingMassnahmeId(coachingId: string): Promise<string | null> {
  const row = await apiGetOneOrNull('/coaching/{id}', { id: coachingId })
  return row?.massnahme_id ?? null
}

export type ParticipantStammdaten = {
  vorname?: string
  nachname?: string
  kundennummer?: string
  strasse?: string
  hausnummer?: string
  plz?: string
  ort?: string
  geburtsdatum?: string
  telefon?: string
  email?: string
}

export type CoachingCreate = {
  id: string
  massnahme_id: string | null
  agentur_id: string | null
  coach_id: string
  organisation_id: string
}

export async function createCoachingForPerson(
  coachingId: string,
  nachname: string,
  vorname: string,
  stammdaten: ParticipantStammdaten = {},
  teilnehmendeId: string = crypto.randomUUID(),
): Promise<void> {
  const { userId, organisationId, variant } = await me()

  await apiPut('/teilnehmende', {
    id: teilnehmendeId,
    vorname,
    nachname,

    kundennummer: stammdaten.kundennummer || null,

    geburtsdatum: stammdaten.geburtsdatum || null,
    email: stammdaten.email ?? '',
    phone: stammdaten.telefon ?? '',
    strasse: stammdaten.strasse ?? '',
    hausnummer: stammdaten.hausnummer ?? '',
    plz: stammdaten.plz ?? '',
    ort: stammdaten.ort ?? '',
    organisation_id: organisationId,
  })

  const coachingRow: CoachingCreate = {
    id: coachingId,

    massnahme_id: null,

    agentur_id: null,
    coach_id: userId,
    organisation_id: organisationId,
  }

  const subtype = descriptionFor(variant).subtype
  if (subtype) await subtype.createCoaching(coachingRow)
  else await apiPut('/coaching', coachingRow)

  try {
    await apiPut('/teilnahme', {
      coaching_id: coachingId,
      teilnehmende_id: teilnehmendeId,
      aufgabe_id: await aufgabeIdFor('stammdaten'),
    })
  } catch (error) {
    throwWriteError('Teilnahme', error as { message: string })
  }

}

export type JourneyPosition = {
  aufgabeName: string | null
  position: number | null
}

export async function getJourneyPosition(coachingId: string): Promise<JourneyPosition> {

  const row = await apiFirst('/teilnahme', {
    coaching_id: coachingId,
    include: ['aufgabe'],
    limit: 1,
  })
  const aufgabeName = row?.aufgabe?.name ?? null
  return {
    aufgabeName,
    position: positionForAufgabeName(aufgabeName),
  }
}

const aufgabeStatusNames = new Map<string, string>()
async function statusNameForAufgabeId(aufgabeId: string): Promise<string | undefined> {
  if (aufgabeStatusNames.size === 0) {
    for (const sub of await apiListAll('/aufgabe', { include: ['status'] })) {
      if (sub.status) aufgabeStatusNames.set(sub.id, sub.status.name)
    }
  }
  return aufgabeStatusNames.get(aufgabeId)
}

export type AdvanceResult = { advanced: boolean }

type JourneyCursor = { aufgabeId: string; position: number }

async function readJourneyCursor(coachingId: string): Promise<JourneyCursor | null> {
  const row = await apiFirst('/teilnahme', {
    coaching_id: coachingId,
    include: ['aufgabe'],
    limit: 1,
  })
  if (!row) {

    console.error(`Aufgabe nicht gesetzt: keine sichtbare Teilnahme für Coaching ${coachingId}.`)
    return null
  }
  const position = positionForAufgabeName(row.aufgabe?.name)
  if (position === null) {

    console.error(
      `Aufgabe nicht gesetzt: unbekannte aktuelle Position "${row.aufgabe?.name ?? '—'}" ` +
        `(Migration evtl. noch nicht angewendet?).`,
    )
    return null
  }
  return { aufgabeId: row.aufgabe_id, position }
}

async function writeAufgabe(coachingId: string, aufgabe: AufgabeKey, fromAufgabeId: string, identity: WriteIdentity): Promise<void> {
  const aufgabeId = await aufgabeIdFor(aufgabe)
  assertWriteIdentity(identity)

  const current = await apiFirst('/teilnahme', { coaching_id: coachingId, limit: 1 })
  assertWriteIdentity(identity)
  if (!current || current.aufgabe_id !== fromAufgabeId) return
  await apiPatch(
    '/teilnahme/{coaching_id}/{teilnehmende_id}',
    { coaching_id: coachingId, teilnehmende_id: current.teilnehmende_id },
    { aufgabe_id: aufgabeId },
    identity,
  )
}

async function advanceGuarded(coachingId: string, aufgabe: AufgabeKey, identity: WriteIdentity): Promise<AdvanceResult> {
  const cursor = await readJourneyCursor(coachingId)
  assertWriteIdentity(identity)
  if (!cursor) return { advanced: false }
  if (AUFGABE[aufgabe].position <= cursor.position) return { advanced: false }
  await writeAufgabe(coachingId, aufgabe, cursor.aufgabeId, identity)
  return { advanced: true }
}

export async function setAufgabe(coachingId: string, aufgabe: AufgabeKey, identity: WriteIdentity = captureWriteIdentity()): Promise<AdvanceResult> {
  return advanceGuarded(coachingId, aufgabe, identity)
}

export async function rewindJourney(
  coachingId: string,
  target: AufgabeKey,
  notBeyond: AufgabeKey,
  identity: WriteIdentity = captureWriteIdentity(),
): Promise<AdvanceResult> {
  const cursor = await readJourneyCursor(coachingId)
  assertWriteIdentity(identity)
  if (!cursor) return { advanced: false }
  if (cursor.position <= AUFGABE[target].position) return { advanced: false }
  if (cursor.position > AUFGABE[notBeyond].position) return { advanced: false }
  await writeAufgabe(coachingId, target, cursor.aufgabeId, identity)
  return { advanced: true }
}

const readTeilnahme = (coachingId: string) => apiFirst('/teilnahme', {
  coaching_id: coachingId, include: ['teilnehmende'], limit: 1,
})
async function stageOf(row: Awaited<ReturnType<typeof readTeilnahme>>): Promise<Stage | null> {
  if (!row?.aufgabe_id) return null
  const [statusName, { variant }] = await Promise.all([statusNameForAufgabeId(row.aufgabe_id), me()])
  return descriptionFor(variant).stageForStatusName(statusName)
}

export async function getParticipantId(coachingId: string): Promise<string | null> {
  const row = await apiFirst('/teilnahme', { coaching_id: coachingId, limit: 1 })
  return row?.teilnehmende_id ?? null
}

export async function getParticipant(coachingId: string): Promise<Participant | null> {
  const row = await readTeilnahme(coachingId)
  return row?.teilnehmende ?? null
}

export type CoachingMeta = {
  stage: Stage | null
  gueltigkeitBeginn: string | null
  gueltigkeitEnde: string | null

  teilnahme: TeilnahmeZeitraum | null
}

export type TeilnahmeZeitraum = {
  zuweisungBeginn: string | null
  zuweisungEnde: string | null
  teilnahmebeginn: string | null
}
export async function loadPersonalDetails(coachingId: string): Promise<{ participant: Participant | null; meta: CoachingMeta | null }> {

  const teilnahme = readTeilnahme(coachingId)
  const [row, stage, window, { variant }] = await Promise.all([
    teilnahme,
    teilnahme.then(stageOf),
    getGueltigkeit(coachingId),
    me(),
  ])
  return {
    participant: row?.teilnehmende ?? null,
    meta: window ? {
      stage,
      gueltigkeitBeginn: noPlaceholder(window.beginn),
      gueltigkeitEnde: noPlaceholder(window.ende),
      teilnahme:
        row && descriptionFor(variant).zuweisungszeitraum
          ? { zuweisungBeginn: row.beginn, zuweisungEnde: row.ende, teilnahmebeginn: row.teilnahmebeginn }
          : null,
    } : null,
  }
}

async function getGueltigkeit(coachingId: string): Promise<CoachingGueltigkeit | null> {
  const { variant } = await me()
  const subtype = descriptionFor(variant).subtype
  if (subtype) return subtype.getGueltigkeit(coachingId)
  const row = await apiGetOneOrNull('/coaching/{id}', { id: coachingId })
  return row ? { beginn: null, ende: null } : null
}
export type CoachingGueltigkeit = { beginn: string | null; ende: string | null }

export type CoachingMassnahme = {
  nummer: string
  bezeichnung: string

  ue_anzahl: number
  kosten: string | number | null
  abschluss: string

}
export type CoachingVertragRef = {
  avgs_nummer: number
  zeitl_umf_max_ue: number
  agentur: { behoerde: string; standort: string } | null
  massnahme: CoachingMassnahme | null
}
export async function getCoachingVertragRef(coachingId: string): Promise<CoachingVertragRef | null> {

  const { variant } = await me()
  return subtypeFor(variant).getCoachingVertragRef(coachingId)
}

export type CoachingVoucherRef = {
  inhalt_gruppen: string
  inhalt_einzel: string
  zeitl_umf_kategorie: string
  zeitl_umf_max_ue: number
  gueltigkeit_beginn: string
  gueltigkeit_ende: string

  max_dauer: string
  massnahme: { nummer: string } | null
}
export async function getCoachingVoucherRef(coachingId: string): Promise<CoachingVoucherRef | null> {

  const { variant } = await me()
  return subtypeFor(variant).getCoachingVoucherRef(coachingId)
}

export async function updateCoachingVoucherFields(
  coachingId: string,
  patch: CoachingVoucherPatch,
): Promise<void> {

  const { variant } = await me()
  await subtypeFor(variant).patchCoaching(coachingId, patch)
}

export type CoachingKostentraeger = {
  strasse: string | null
  hausnummer: string | null
  plz: string | null
  ort: string | null
  zeichen: string | null
}
export async function getCoachingKostentraeger(coachingId: string): Promise<CoachingKostentraeger | null> {
  const row = await apiGetOneOrNull('/coaching/{id}', { id: coachingId }, {
    include: ['agentur'],
  })
  if (!row) return null
  return {
    strasse: row.agentur?.strasse ?? null,
    hausnummer: row.agentur?.hausnummer ?? null,
    plz: row.agentur?.plz ?? null,
    ort: row.agentur?.ort ?? null,
    zeichen: row.org_zeichen ?? null,
  }
}

export async function updateCoachingOrgZeichen(coachingId: string, orgZeichen: string | null): Promise<void> {
  await apiPatch('/coaching/{id}', { id: coachingId }, { org_zeichen: orgZeichen })
}

export async function getTeilnahmeDates(
  coachingId: string,
): Promise<{ beginn: string | null; ende: string | null }> {
  const row = await apiFirst('/teilnahme', { coaching_id: coachingId, limit: 1 })
  return { beginn: row?.beginn ?? null, ende: row?.ende ?? null }
}

export async function updateParticipantStammdaten(
  coachingId: string,
  fields: ParticipantStammdaten,
  identity: WriteIdentity = captureWriteIdentity(),
): Promise<void> {
  const protectedFields = { vorname: 'Vorname', nachname: 'Nachname', kundennummer: 'Kundennummer', geburtsdatum: 'Geburtsdatum' } as const
  const empty = (Object.keys(protectedFields) as (keyof typeof protectedFields)[]).filter((key) => fields[key]?.trim() === '')
  if (empty.length) throw new Error(`${empty.map((key) => protectedFields[key]).join(', ')}: Darf nicht leer sein.`)
  const teilnehmendeId = await getParticipantId(coachingId)
  if (!teilnehmendeId) throw new Error('Kein zugänglicher Teilnehmender zu diesem Coaching gefunden.')
  const patch: Record<string, unknown> = {}
  for (const key of Object.keys(protectedFields) as (keyof typeof protectedFields)[]) {
    if (fields[key] !== undefined) patch[key] = fields[key]
  }
  if (fields.strasse !== undefined) patch.strasse = fields.strasse
  if (fields.hausnummer !== undefined) patch.hausnummer = fields.hausnummer
  if (fields.plz !== undefined) patch.plz = fields.plz
  if (fields.ort !== undefined) patch.ort = fields.ort
  if (fields.telefon !== undefined) patch.phone = fields.telefon
  if (fields.email !== undefined) patch.email = fields.email
  if (Object.keys(patch).length === 0) return
  await apiPatch('/teilnehmende/{id}', { id: teilnehmendeId }, patch, identity)
}

export async function promoteAvgs(coachingId: string, promo: AvgsPromotion): Promise<void> {

  const teilnehmendeId = await getParticipantId(coachingId)
  if (!teilnehmendeId) throw new Error('Kein Teilnehmender zu diesem Coaching gefunden.')

  const ag = await apiFirst('/agentur', { standort: promo.agentur.standort, limit: 1 })
  let agenturId = ag?.id
  if (!agenturId) {
    try {
      agenturId = (await apiPost('/agentur', promo.agentur)).id
    } catch (error) {

      if (!isUniqueViolation(error)) throw error
      const winner = await apiFirst('/agentur', { standort: promo.agentur.standort, limit: 1 })
      if (!winner) throw error
      agenturId = winner.id
    }
  }

  const { variant } = await me()
  await subtypeFor(variant).patchCoaching(coachingId, { ...promo.coaching, agentur_id: agenturId })
  await apiPatch('/teilnehmende/{id}', { id: teilnehmendeId }, promo.teilnehmende)
}

export const UNTERSCHRIEBEN_REFUSAL =
  'Diese Person hat bereits unterschrieben. Unterschriebene Unterlagen sind Prüfnachweise und können nicht gelöscht werden.'

export const NICHT_BERECHTIGT_REFUSAL =
  'Nur Owner dieser Organisation oder der zuständige Coach können dieses Coaching löschen.'

export class CoachingDeleteRefused extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'CoachingDeleteRefused'
  }
}

export async function deleteCoachingCascade(coachingId: string): Promise<void> {
  const { variant } = await me()
  const status = await descriptionFor(variant).getLoeschStatus?.(coachingId)
  if (status && !status.darfLoeschen) throw new CoachingDeleteRefused(NICHT_BERECHTIGT_REFUSAL)
  if (status?.unterschrieben) throw new CoachingDeleteRefused(UNTERSCHRIEBEN_REFUSAL)
  const teilnehmendeId = await getParticipantId(coachingId)

  const docs = await apiListAll('/dokumente', { coaching_id: coachingId })
  const signed = docs.length > 0 ? await apiListAll('/signatur', { dokument_id__in: docs.map((d) => d.id) }) : []
  if (signed.length > 0) throw new CoachingDeleteRefused(UNTERSCHRIEBEN_REFUSAL)

  const prefix = `coaching/${coachingId}`
  const legacy = await listFiles(prefix)
  const owned = docs.map((d) => d.bucket_address).filter((a): a is string => Boolean(a))
  const paths = [...new Set([...legacy, ...owned])]
  for (const path of paths) {
    try {
      await removeFile(path)
    } catch (error) {

      if (error instanceof ApiError && error.status === 409) {
        throw new CoachingDeleteRefused(error.detailText || UNTERSCHRIEBEN_REFUSAL)
      }
      throw error
    }
  }

  const remaining = await listFiles(prefix)
  if (remaining.length > 0) {
    throw new Error(`Speicher-Dateien konnten nicht vollständig gelöscht werden: ${remaining.join(', ')}`)
  }

  for (const doc of docs) await apiDelete('/dokumente/{id}', { id: doc.id })

  await deleteCoachingRows(coachingId, teilnehmendeId)
}

export async function deleteCoachingRows(
  coachingId: string,
  teilnehmendeId: string | null,
): Promise<void> {

  const teilnahmen = await apiListAll('/teilnahme', { coaching_id: coachingId })
  for (const t of teilnahmen) {
    await apiDelete('/teilnahme/{coaching_id}/{teilnehmende_id}', {
      coaching_id: t.coaching_id,
      teilnehmende_id: t.teilnehmende_id,
    })
  }

  await apiDelete('/coaching/{id}', { id: coachingId })
  if (await apiGetOneOrNull('/coaching/{id}', { id: coachingId })) {
    throw new Error('Coaching konnte nicht gelöscht werden (keine Berechtigung?).')
  }

  if (teilnehmendeId) {
    const remaining = await apiListAll('/teilnahme', { teilnehmende_id: teilnehmendeId })
    if (remaining.length === 0) {
      await apiDelete('/teilnehmende/{id}', { id: teilnehmendeId })
    }
  }
}

export type AvgsCandidate = { coachingId: string; vorname: string; nachname: string; ready: boolean }

type CandidateRow = Omit<AvgsCandidate, 'ready'> & {
  massnahmeNummer: string
  aufgabeName: string | null
}

async function listCandidateRows(
  organisationId: string,
  statusId: string,
): Promise<CandidateRow[]> {
  const aufgaben = await apiListAll('/aufgabe', { status_id: statusId })
  if (aufgaben.length === 0) return []
  const aufgabeById = new Map(aufgaben.map((sub) => [sub.id, sub]))
  const teilnahmen = (
    await Promise.all(
      aufgaben.map((sub) => apiListAll('/teilnahme', { aufgabe_id: sub.id, include: ['teilnehmende'] })),
    )
  ).flat()
  if (teilnahmen.length === 0) return []
  const byCoaching = new Map(teilnahmen.map((t) => [t.coaching_id, t]))

  const coachings = await apiListAll('/coaching', {
    organisation_id: organisationId,
    include: ['massnahme'],
  })
  return coachings.flatMap((coaching) => {
    const participation = byCoaching.get(coaching.id)
    if (!participation?.teilnehmende) return []
    const participant = participation.teilnehmende
    return [{
      coachingId: coaching.id,
      vorname: participant.vorname,
      nachname: participant.nachname,
      massnahmeNummer: coaching.massnahme?.nummer ?? '',
      aufgabeName: participation.aufgabe_id ? aufgabeById.get(participation.aufgabe_id)?.name ?? null : null,
    }]
  })
}

export async function listAvgsCandidates(): Promise<AvgsCandidate[]> {
  const { organisationId } = await me()

  const data = await listCandidateRows(organisationId, await statusIdFor('avgs'))
  return data.map(({ coachingId, vorname, nachname, aufgabeName }) => ({
    coachingId,
    vorname,
    nachname,
    ready: aufgabeName === AUFGABE.avgsUpload.dbName,
  }))
}

export type BewilligungCandidate = {
  coachingId: string
  vorname: string
  nachname: string
  massnahmeNummer: string
  ready: boolean
}

export async function listBewilligungCandidates(): Promise<BewilligungCandidate[]> {
  const { organisationId } = await me()
  const data = await listCandidateRows(organisationId, await statusIdFor('bewilligung'))
  return data.map(({ coachingId, vorname, nachname, massnahmeNummer, aufgabeName }) => ({
    coachingId,
    vorname,
    nachname,
    massnahmeNummer,
    ready: aufgabeName === AUFGABE.bewilligung.dbName,
  }))
}
